"use client";

import { MouseEvent } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SelectArticle } from "@/db/schema";
import {
  serverAddEmbeddings,
  serverDelete,
  serverDeleteEmbeddings,
} from "./action";

async function handleDelete(
  e: MouseEvent<HTMLDivElement>,
  article?: SelectArticle,
) {
  e.preventDefault();
  e.stopPropagation();

  const statusResponse = article && (await serverDelete(article));
  statusResponse?.status === "Success"
    ? toast.success(statusResponse?.message)
    : toast.error(statusResponse?.message);
}

async function handleAddEmbeddings(
  e: MouseEvent<HTMLDivElement>,
  article?: SelectArticle,
) {
  e.preventDefault();
  e.stopPropagation();

  const statusResponse = article && (await serverAddEmbeddings(article));
  statusResponse?.status === "Success"
    ? toast.success(statusResponse?.message)
    : toast.error(statusResponse?.message);
}

async function handleDeleteEmbeddings(
  e: MouseEvent<HTMLDivElement>,
  article?: SelectArticle,
) {
  e.preventDefault();
  e.stopPropagation();

  const statusResponse = article && (await serverDeleteEmbeddings(article));
  statusResponse?.status === "Success"
    ? toast.success(statusResponse?.message)
    : toast.error(statusResponse?.message);
}

export const columns: ColumnDef<SelectArticle>[] = [
  {
    accessorKey: "id",
    header: "ID",
    cell: (ctx) => {
      return ctx.getValue<number>().toString().slice(0, 5);
    },
  },
  {
    accessorKey: "title",
    header: "Title",
  },
  {
    accessorKey: "slug",
    header: "Slug",
  },
  {
    accessorKey: "createdAt",
    header: "Created at",
  },
  {
    accessorKey: "updatedAt",
    header: "Last updated",
  },
  {
    accessorKey: "published",
    header: "Published",
    cell: (ctx) => {
      return ctx.getValue<boolean>() ? "Yes" : "No";
    },
  },
  {
    accessorKey: "embeddingIds",
    header: "Has Embeddings",
    cell: (ctx) => {
      const values = ctx.getValue<string[]>();
      return values?.length && values.filter((i) => i).length ? "Yes" : "No";
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const article = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem>Edit article</DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => handleDelete(e, article)}
              className="focus:bg-destructive focus:text-destructive-foreground"
            >
              Delete article
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={Boolean(article.embeddingIds?.length)}
              onClick={(e) => handleAddEmbeddings(e, article)}
            >
              <Wand2 className="mr-2 h-4 w-4" /> Create article embeddings
            </DropdownMenuItem>
            <DropdownMenuItem
              className="focus:bg-destructive"
              // disabled={!Boolean(article.embeddingIds?.length)}
              onClick={(e) => handleDeleteEmbeddings(e, article)}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete embeddings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
